// Item logistics: hopper transfer + dropper/dispenser firing (RED-2, M15.2.1).
// PURE (ENG-1) — the host adapts neighbouring block entities into `TransferTarget`s
// and enacts what a dropper/dispenser hands back (spawn a projectile, eject an
// item). See docs/redstone-semantics.md §"Item logistics".
//
// Timings (RED-2): a hopper moves one item, then holds an 8-gt cooldown, so a
// hopper line carries exactly 1 item / 8 gt. Droppers/dispensers act on the rising
// edge of redstone power (edge detection is the host's; this file is the effect).
import { type ItemStack, type Slots, emptyStack, isEmpty, cloneStack } from './inventory';
import { insert } from './transaction';
import { type ArrayContainer, firstFilled } from './containers/array';
import { type Dir, OPPOSITE } from './redstone/types';
import { REDSTONE } from '../config';
import type { Rng } from '../engine/rng';

/** A neighbouring container a hopper/dropper can pull from or push into. The host
 *  builds these from block entities (a plain chest is symmetric; a furnace exposes
 *  only its output for extraction and routes inserts by the entry face). */
export interface TransferTarget {
  slots: Slots;
  /** Slot indices an item may be pulled FROM (furnace → its output slot only). */
  extract: readonly number[];
  /** Slot indices an item entering through container face `face` may go INTO
   *  (furnace: top face → smelt input, side face → fuel, bottom → nothing). */
  insertFrom(face: Dir): readonly number[];
}

/** A symmetric container (chest/hopper/dropper/dispenser): every slot both ways. */
export function fullTarget(slots: Slots): TransferTarget {
  const all = allSlots(slots.size);
  return { slots, extract: all, insertFrom: () => all };
}

function allSlots(size: number): readonly number[] {
  return Array.from({ length: size }, (_, i) => i);
}

/** Move one item from the first non-empty `fromRange` slot of `from` into `toRange`
 *  of `to`. Returns true iff exactly one item moved (all-or-nothing per item). */
function moveOne(from: Slots, fromRange: readonly number[], to: Slots, toRange: readonly number[]): boolean {
  for (const fi of fromRange) {
    const src = from.get(fi);
    if (isEmpty(src)) continue;
    const remainder = insert(to, { ...cloneStack(src), count: 1 }, toRange);
    if (!isEmpty(remainder)) continue; // no room for it there → try the next slot
    from.set(fi, src.count <= 1 ? emptyStack() : { ...src, count: src.count - 1 });
    return true;
  }
  return false;
}

/**
 * Advance one hopper by one game tick. Pulls one item from the container above and
 * pushes one into the container it faces, then holds an 8-gt cooldown (RED-2). A
 * `locked` hopper (powered) does nothing. Returns true iff any item moved (so the
 * host can flag comparators reading this hopper dirty). `selfSlots` is a `Slots`
 * view of `self` (built by the caller with the right maxStack).
 */
export function tickHopper(
  self: ArrayContainer,
  selfSlots: Slots,
  facing: Dir,
  above: TransferTarget | null,
  faced: TransferTarget | null,
  locked: boolean,
): boolean {
  if (self.cooldown > 0) self.cooldown--;
  if (self.cooldown > 0) return false; // still cooling down
  if (locked) return false;

  let moved = false;
  const selfRange = allSlots(selfSlots.size);
  // Push toward `facing`: the item enters the faced container through its far side.
  if (faced && firstFilled(self) >= 0) {
    if (moveOne(selfSlots, selfRange, faced.slots, faced.insertFrom(OPPOSITE[facing]))) moved = true;
  }
  // Pull down from the container directly above.
  if (above && moveOne(above.slots, above.extract, selfSlots, selfRange)) moved = true;

  if (moved) self.cooldown = REDSTONE.HOPPER_COOLDOWN;
  return moved;
}

/** Index of a uniformly-random non-empty slot, or -1 (dropper/dispenser pick). */
export function pickRandomFilled(c: ArrayContainer, rng: Rng): number {
  const filled: number[] = [];
  for (let i = 0; i < c.items.length; i++) {
    const s = c.items[i];
    if (s && !isEmpty(s)) filled.push(i);
  }
  return filled.length === 0 ? -1 : filled[rng.nextInt(filled.length)]!;
}

/**
 * A dropper firing (rising edge). Moves one item from a random slot into the faced
 * container; if it faces a container with no room it does nothing; if it faces no
 * container (air) it ejects one item, returned for the host to spawn as an entity.
 */
export function dropperFire(
  self: ArrayContainer,
  selfSlots: Slots,
  facing: Dir,
  faced: TransferTarget | null,
  rng: Rng,
): ItemStack | null {
  const slot = pickRandomFilled(self, rng);
  if (slot < 0) return null;
  const src = selfSlots.get(slot);
  const one = { ...cloneStack(src), count: 1 };
  const consume = (): void => selfSlots.set(slot, src.count <= 1 ? emptyStack() : { ...src, count: src.count - 1 });
  if (faced) {
    const remainder = insert(faced.slots, one, faced.insertFrom(OPPOSITE[facing]));
    if (!isEmpty(remainder)) return null; // container full → nothing happens
    consume();
    return null;
  }
  consume();
  return one; // eject into the world
}

/** What a dispenser does with an item (RED-2 behaviour table). */
export type DispenseKind = 'shoot' | 'eject';

export interface DispenseResult {
  kind: DispenseKind;
  /** The consumed item id (for the host to shoot/eject). */
  item: number;
}

/** Classify a dispensed item. Projectiles are shot; everything else is ejected as
 *  a dropper would (the exotic behaviours — fluids, bone meal, armour equips,
 *  spawn eggs — are deferred to M15.2-remainder; see docs). */
export function dispenseKind(itemName: string | undefined): DispenseKind {
  switch (itemName) {
    case 'arrow': return 'shoot';
    default: return 'eject';
  }
}

/**
 * A dispenser firing (rising edge): pick a random slot, consume one item, and hand
 * the host what to enact. Returns null when empty.
 */
export function dispenserFire(
  self: ArrayContainer,
  selfSlots: Slots,
  nameOf: (item: number) => string | undefined,
  rng: Rng,
): DispenseResult | null {
  const slot = pickRandomFilled(self, rng);
  if (slot < 0) return null;
  const src = selfSlots.get(slot);
  const item = src.item;
  selfSlots.set(slot, src.count <= 1 ? emptyStack() : { ...src, count: src.count - 1 });
  return { kind: dispenseKind(nameOf(item)), item };
}
